import { Component, EventEmitter, OnDestroy, OnInit, Output, inject } from '@angular/core';
import {
  ControlContainer,
  FormControl,
  FormGroup,
  FormGroupDirective,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Subscription } from 'rxjs';
import { distinctUntilChanged, tap } from 'rxjs/operators';
import { IceSelectBoxComponent } from '@app/shared/components/molecules/ice-select-box/ice-select-box.component';
import { IceDatePickerComponent } from '@app/shared/components/molecules/ice-date-picker/ice-date-picker.component';
import { dateFns } from '@core/date-utils';

const MaxDaysInTheFuture = 140;

@Component({
  selector: 'app-mbb-activation-date',
  imports: [FormsModule, ReactiveFormsModule, IceSelectBoxComponent, IceDatePickerComponent],
  templateUrl: './mbb-activation-date.component.html',
  styleUrl: './mbb-activation-date.component.scss',
  viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
})
export class MbbActivationDate implements OnInit, OnDestroy {
  private formGroupDirective = inject(FormGroupDirective);

  @Output() isInvalid = new EventEmitter<boolean>();
  @Output() activationDateChange = new EventEmitter<Date | null>();

  form: FormGroup;
  subscriptions$: Subscription[] = [];
  isEarliestDateControl = new FormControl<boolean>(false);
  minDate: Date = dateFns.addDays(new Date(), 1);
  maxDate: Date = dateFns.addDays(new Date(), MaxDaysInTheFuture);

  static buildActivationDateControl(initialDate: Date | null): FormControl<Date | null> {
    return new FormControl<Date | null>(initialDate, [Validators.required, this.dateValidator]);
  }

  get activationDateControl(): FormControl<Date | null> {
    return this.form.get('activationDate') as FormControl<Date | null>;
  }

  get earliestDateLabel(): string {
    const today = new Date();
    return today.toLocaleDateString();
  }

  ngOnInit(): void {
    this.form = this.formGroupDirective.form;

    if (!this.form.get('activationDate')) {
      this.form.addControl('activationDate', MbbActivationDate.buildActivationDateControl(null));
    }

    const hasDate = !!this.activationDateControl.value;
    this.isEarliestDateControl.setValue(!hasDate, { emitEvent: false });
    if (!hasDate) {
      this.activationDateControl.clearValidators();
      this.activationDateControl.updateValueAndValidity({ emitEvent: false });
    } else {
      this.activationDateControl.setValidators([Validators.required]);
      this.activationDateControl.updateValueAndValidity({ emitEvent: false });
    }

    this.subscriptions$.push(
      this.activationDateControl.valueChanges
        .pipe(
          distinctUntilChanged(),
          tap((value: Date | null) => this.activationDateChange.emit(value)),
          tap(() => this.isInvalid.emit(this.activationDateControl.invalid))
        )
        .subscribe()
    );

    this.subscriptions$.push(
      this.isEarliestDateControl.valueChanges
        .pipe(
          distinctUntilChanged(),
          tap(isEarliest => {
            if (isEarliest) {
              this.activationDateControl.reset(null);
              this.activationDateControl.disable({ emitEvent: false });
            } else {
              this.activationDateControl.enable({ emitEvent: false });
            }
          })
        )
        .subscribe()
    );

    this.isInvalid.emit(this.activationDateControl.invalid);
  }

  ngOnDestroy(): void {
    this.activationDateChange.emit(this.activationDateControl.value);
    this.isInvalid.emit(this.activationDateControl.invalid);
    this.subscriptions$.forEach(s => s.unsubscribe());
  }

  get errorMessage(): string {
    if (this.activationDateControl.hasError('required')) {
      return 'Aktiveringsdato er påkrevd';
    } else if (this.activationDateControl.hasError('invalidDate')) {
      return 'Ugyldig dato';
    } else if (this.activationDateControl.hasError('outOfRange')) {
      return `Datoen må være innen ${MaxDaysInTheFuture} dager fra i dag`;
    }
    return '';
  }

  private static dateValidator(control: FormControl<Date | null>): { [key: string]: boolean } | null {
    const dateValue = control.value;

    if (!dateValue) {
      return { invalidDate: true };
    }

    const tomorrow = dateFns.addDays(new Date(), 1);
    const maxDate = dateFns.addDays(new Date(), MaxDaysInTheFuture);
    if (dateValue && (dateFns.isBefore(new Date(dateValue), tomorrow) || dateFns.isAfter(new Date(dateValue), maxDate))) {
      return { outOfRange: true };
    }

    return null;
  }

  get activationTimeBoxLabel(): string {
    return `Inntil ${MaxDaysInTheFuture} dager frem i tid`;
  }
}
